import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateTime } from 'luxon';
import { take } from 'rxjs';

import { KSeFApiService } from 'shared/services/api/ksef.api.service';
import { SnackbarService } from 'shared/services/snackbar.service';

export interface KsefSyncDialogData {
  /** Czy połączenie jest aktywne */
  isAuthorized: boolean;
}

export interface KsefSyncDialogResult {
  /** Czy synchronizacja się powiodła */
  synced: boolean;
  /** Wynik synchronizacji */
  data?: any;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatLuxonDateModule,
    MatProgressSpinnerModule
  ],
  selector: 'app-ksef-sync-dialog',
  templateUrl: './ksef-sync-dialog.component.html',
  styleUrl: './ksef-sync-dialog.component.scss'
})
export class KsefSyncDialogComponent {

  /** Minimalna data - 1 lutego 2026 */
  public minDate = DateTime.fromISO('2026-02-01');
  /** Maksymalna data - wczoraj */
  public maxDate = DateTime.now().minus({ days: 1 });

  /** Czy urządzenie mobilne (touch UI dla datepickerów) */
  public isMobile = window.innerWidth < 750;

  public isSyncing = false;
  public syncResult: any = null;
  public syncError: string | null = null;

  /** Maksymalny zakres dat w dniach */
  public readonly maxRangeDays = 31;

  public form = new FormGroup({
    dateFrom: new FormControl<DateTime | null>(this.minDate),
    dateTo: new FormControl<DateTime | null>(this.maxDate)
  });

  /** Czy wybrany zakres przekracza maksymalny limit */
  get isRangeExceeded(): boolean {
    const from = this.form.value.dateFrom;
    const to = this.form.value.dateTo;
    if (!from || !to) return false;
    return to.diff(from, 'days').days > this.maxRangeDays;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KsefSyncDialogData,
    private _dialogRef: MatDialogRef<KsefSyncDialogComponent>,
    private _ksefApiService: KSeFApiService,
    private _snackbarService: SnackbarService
  ) { }

  public startSync(): void {
    if (this.isSyncing) return;

    const dateFrom = this.form.value.dateFrom;
    const dateTo = this.form.value.dateTo;

    if (!dateFrom || !dateTo) {
      this._snackbarService.showSnackBar('Wybierz zakres dat', 'error', 3000);
      return;
    }

    if (dateFrom > dateTo) {
      this._snackbarService.showSnackBar('Data początkowa nie może być późniejsza niż końcowa', 'error', 3000);
      return;
    }

    this.isSyncing = true;
    this.syncResult = null;
    this.syncError = null;

    this._ksefApiService.syncInvoices({
      full: false,
      subjectTypes: ['subject1', 'subject2'],
      dateFrom: dateFrom.toISO()!,
      dateTo: dateTo.endOf('day').toISO()!
    })
    .pipe(take(1))
    .subscribe({
      next: (response: any) => {
        this.isSyncing = false;
        this.syncResult = response?.data;

        const totalSynced = (response?.data?.income?.inserted || 0) + (response?.data?.expense?.inserted || 0);
        const totalUpdated = (response?.data?.income?.updated || 0) + (response?.data?.expense?.updated || 0);

        if (totalSynced > 0 || totalUpdated > 0) {
          this._snackbarService.showSnackBar(
            `Zsynchronizowano ${totalSynced} nowych faktur` + (totalUpdated > 0 ? ` (${totalUpdated} zaktualizowanych)` : ''),
            'success', 5000
          );
        } else {
          this._snackbarService.showSnackBar('Brak nowych faktur do synchronizacji', 'info', 3000);
        }
      },
      error: (error) => {
        this.isSyncing = false;
        this.syncError = error?.error?.message || 'Wystąpił błąd podczas synchronizacji z KSeF';
        this._snackbarService.showSnackBar(this.syncError!, 'error', 5000);
      }
    });
  }

  public close(): void {
    this._dialogRef.close({
      synced: !!this.syncResult,
      data: this.syncResult
    } as KsefSyncDialogResult);
  }
}
